import React from 'react';
import { useSelector } from 'react-redux';
import { ButtonBase, Heading } from '@americanexpress/dls-react';
import { IconPlus } from '@americanexpress/dls-icons';
import { useRouteParams } from '@/context/routeParamsContext';
import { useAnalyticsContext } from '@/context/analyticsContext';
import { useLoungeProviders } from '@/hooks/loungesHooks/loungesHelperHooks';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import {
  formatText,
  normalizeProviderKey,
  resolveImageUrl,
} from '@/helpers/utils';

const LoungeDetailsProviders = () => {
  const { loungeId } = useRouteParams();
  const providers = useLoungeProviders(loungeId);
  const { providers: providerContent } = useCommonContent();
  const oneCmsImagesBaseUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const { customTrack } = useAnalyticsContext();
  if (!providerContent?.header || providers.length === 0) return null;

  const handleProviderClick = (event, provider) => {
    customTrack({
      event,
      detail: `${provider.name.toLowerCase()} provider details`,
    });
  };

  return (
    <section
      data-qe-id="lounge-providers"
      className="col-xs-12 margin-4-t pad-4-b"
    >
      <Heading
        level={2}
        variant="sans-medium-regular"
        className="margin-3-b tw-text-gray-900"
      >
        {providerContent.header}
      </Heading>
      <ul className="scrollbar-hidden pad-0-lr scroll-x-sm-down tw-flex tw-gap-4 md_tw-flex-wrap">
        {providers.map((provider) => {
          const logoSrc = resolveImageUrl(provider.logo, oneCmsImagesBaseUrl);
          if (!provider.name || !logoSrc) return null;

          const providerSlug =
            normalizeProviderKey(provider.providerKey) ||
            normalizeProviderKey(provider.name);

          return (
            <li
              key={providerSlug}
              className="tw-h-35 tw-w-53 tw-min-w-53 tw-shrink-0 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-gray-300 tw-bg-gray-100"
            >
              <ButtonBase
                type="button"
                data-qe-id={`provider-card-${providerSlug}`}
                className="tw-relative tw-flex tw-h-full tw-w-full tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-overflow-hidden tw-px-8 tw-py-6"
                aria-label={
                  providerContent.cardAriaLabel
                    ? formatText(providerContent.cardAriaLabel, {
                        providerName: provider.name,
                      })
                    : provider.name
                }
                reactlytics={{ disable: true }}
                onClick={(event) => handleProviderClick(event, provider)}
              >
                <img
                  src={logoSrc}
                  alt=""
                  className="tw-aspect-provider-logo tw-h-23 tw-w-full tw-self-stretch tw-object-contain tw-opacity-60 tw-mix-blend-multiply tw-contrast-125 tw-grayscale"
                />
                <span className="tw-absolute tw-bottom-0 tw-right-0 tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-tl-lg tw-border-l tw-border-t tw-border-gray-300 tw-bg-white">
                  <IconPlus
                    aria-hidden="true"
                    color="brand"
                    size="xs"
                    isFilled
                  />
                </span>
              </ButtonBase>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default LoungeDetailsProviders;
